/**
 * Log time, or correct time already logged.
 *
 * One sheet for both jobs, because they are the same seven fields and a phone
 * has no room for two nearly identical screens. Reached from the + in the tab
 * bar (anywhere in the app), from a project, and by tapping an entry.
 *
 * The + exists because the quick-log row on the Time screen can only log
 * against *now*. The thing you actually need on a phone is the entry you forgot
 * on Tuesday, which means a date field, which means a sheet.
 */

import { useMemo, useState } from "react";

import { itemProjectId } from "@/components/ItemRow";
import { KindPicker } from "@/components/KindPicker";
import { defaultKind, fitKind, kindChoices, kindFields, kindIn, type EntryKind } from "@/lib/billing";
import { useToast } from "@/context/ToastContext";
import { useAllItems, useAllPhases, useProjects } from "@/hooks/useData";
import { effectiveRate, useClients, useTimeEntries, useTimeMutations } from "@/hooks/useTime";
import { groupTasksByPhase } from "@/lib/taskOptions";
import { useActivities } from "@/lib/theme";
import { isTravelActivity, loadTravel, travelRate } from "@/lib/travel";
import { entryDayKey, parseDuration, toIsoLocal } from "@/lib/timeRollup";
import type { TimeEntry } from "@/lib/types";

import { PickerRow, Sheet, TaskPicker, durationLabel, field, tapGhost, tapPrimary } from "./index";

const CHIPS = [15, 30, 60, 120];

/** Where on a past day to place an entry that has no clock time of its own. */
const BACKDATE_START_HOUR = 9;

export interface LogSheetRequest {
  projectId?: string;
  itemId?: string;
  minutes?: number;
  /** Prefill for a calendar meeting being logged: its title, and exactly when
   *  it happened rather than "ending now". */
  note?: string;
  start?: string;
  /** Present when correcting an entry rather than adding one. */
  entry?: TimeEntry;
}

export function LogSheet({ request, onClose }: { request: LogSheetRequest; onClose: () => void }) {
  const toast = useToast();
  const { data: projects = [] } = useProjects();
  const { data: clients = [] } = useClients();
  const { data: items = [] } = useAllItems();
  const { data: phases = [] } = useAllPhases();
  const { data: entries = [] } = useTimeEntries();
  const { logEntry, updateEntry, deleteEntry } = useTimeMutations();
  const [activities] = useActivities();

  const editing = request.entry;

  const [projectId, setProjectId] = useState(editing?.project || request.projectId || "");
  const [itemId, setItemId] = useState(editing?.item || request.itemId || "");
  const [note, setNote] = useState(editing?.note || request.note || "");
  const [activity, setActivity] = useState(editing?.category || "");
  const [day, setDay] = useState(
    editing ? entryDayKey(editing) : request.start ? toIsoLocal(new Date(request.start)) : toIsoLocal(new Date()),
  );
  const [minutes, setMinutes] = useState(
    editing ? Math.max(1, Math.round((editing.durationSeconds || 0) / 60)) : (request.minutes ?? 30),
  );
  // An entry being corrected has to SHOW its current length, or the sheet opens
  // claiming nothing about the number you came to change. A length that matches
  // a chip highlights it; anything else is written into the custom box as h:mm.
  const [custom, setCustom] = useState(() => {
    if (!editing) return "";
    const mins = Math.max(1, Math.round((editing.durationSeconds || 0) / 60));
    return CHIPS.includes(mins) ? "" : `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, "0")}`;
  });
  /** An entry being corrected opens on its own kind; a new one follows the project. */
  const [kindPick, setKindPick] = useState<EntryKind | null>(() => (editing ? kindIn(editing, projects) : null));
  const [saving, setSaving] = useState(false);

  const selectable = projects.filter((p) => p.status !== "archived" && p.kind !== "inbox");
  const project = projects.find((p) => p.id === projectId);
  const hasClient = !!effectiveRate(project, clients).clientId;
  const kind = fitKind(kindPick ?? defaultKind(project, hasClient), project, hasClient);
  const taskGroups = useMemo(
    () => groupTasksByPhase(items, phases, projectId || undefined, itemProjectId),
    [items, phases, projectId],
  );
  const task = items.find((i) => i.id === itemId);
  // An entry whose activity has since left the list still shows it.
  const activityOptions = activity && !activities.includes(activity) ? [activity, ...activities] : activities;

  /** Picking an activity fills an empty description, as on the Today card. */
  function chooseActivity(next: string) {
    setActivity(next);
    const written = note.trim();
    if (!written || activities.includes(written)) setNote(next);
  }

  // A typed length wins over the selected chip; clearing the box falls back to it.
  // Any text in the box means "custom", valid yet or not. Deciding from the
  // parse alone meant a half-typed "1:" (not a length yet) fell back to the
  // selected chip mid-keystroke: the chip lit up and the button offered its 30m.
  const hasCustom = custom.trim() !== "";
  const typed = hasCustom ? parseDuration(custom) : null;
  const customOk = typed !== null && typed > 0;
  const effective = hasCustom ? (customOk ? typed : 0) : minutes;

  /**
   * Where the entry sits on the clock.
   *
   * Today's work ends *now* — that is what "I just did 30 minutes" means, and
   * it keeps the day's list in the order things happened. A past day has no
   * "now" to hang off, so entries stack from 9am in the order they are added
   * rather than all piling onto the same minute.
   */
  function span(mins: number): { start: string; end: string } {
    // A meeting keeps its own clock time, as long as it is still on its day.
    if (!editing && request.start && toIsoLocal(new Date(request.start)) === day) {
      const start = new Date(request.start);
      return { start: start.toISOString(), end: new Date(start.getTime() + mins * 60_000).toISOString() };
    }
    const today = toIsoLocal(new Date());
    if (day === today) {
      const end = new Date();
      return { start: new Date(end.getTime() - mins * 60_000).toISOString(), end: end.toISOString() };
    }
    const already = entries
      .filter((e) => e.id !== editing?.id && entryDayKey(e) === day)
      .reduce((n, e) => n + (e.durationSeconds || 0), 0);
    const [y, m, d] = day.split("-").map(Number);
    const start = new Date(y, m - 1, d, BACKDATE_START_HOUR, 0, 0);
    start.setSeconds(start.getSeconds() + already);
    return { start: start.toISOString(), end: new Date(start.getTime() + mins * 60_000).toISOString() };
  }

  async function save() {
    if (hasCustom && !customOk) {
      toast("Enter a length like 1:30, 90 or 1.5h");
      return;
    }
    if (effective <= 0) return;
    setSaving(true);
    const r = effectiveRate(project, clients);
    // Only Billable time keeps a rate: an entry moved to another kind drops it,
    // and one moved back to Billable keeps its old rate or takes the current one.
    const { start, end } = span(effective);

    try {
      if (editing) {
        // Travel bills at a share of the rate, so changing the activity to or
        // from travel moves the rate too. The full rate is recovered from the
        // entry's own travelPct, as the desk's editor does.
        const travel = loadTravel();
        const full = editing.rateSnapshot != null && editing.travelPct
          ? Math.round((editing.rateSnapshot / (editing.travelPct / 100)) * 100) / 100
          : editing.rateSnapshot ?? r.rate;
        const k = kindFields(kind, full);
        const isTravel = k.rate != null && isTravelActivity(activity, travel) && travel.pct < 100;
        await updateEntry.mutateAsync({
          id: editing.id,
          project: projectId || null,
          item: itemId || null,
          client: r.clientId || null,
          start, end,
          durationSeconds: effective * 60,
          note,
          category: activity,
          billable: k.billable,
          internal: k.internal,
          rateSnapshot: k.rate != null && isTravel ? travelRate(k.rate, travel.pct) : k.rate,
          travelPct: isTravel ? travel.pct : null,
        } as unknown as Partial<TimeEntry> & { id: string });
        toast("Entry updated");
      } else {
        const k = kindFields(kind, r.rate);
        await logEntry.mutateAsync({
          projectId: projectId || undefined,
          itemId: itemId || undefined,
          start, end,
          durationSeconds: effective * 60,
          note, category: activity || undefined, billable: k.billable, internal: k.internal, source: "manual",
          rate: k.rate, currency: r.currency, clientId: r.clientId,
        });
        toast(`Logged ${durationLabel(effective)}`);
      }
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Could not save that entry");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!editing) return;
    setSaving(true);
    try {
      await deleteEntry.mutateAsync(editing.id);
      toast("Entry deleted");
      onClose();
    } catch {
      toast("Could not delete that entry");
      setSaving(false);
    }
  }

  return (
    <Sheet
      title={editing ? "Edit entry" : "Log time"}
      onClose={onClose}
      footer={
        <div style={{ display: "flex", gap: 10 }}>
          {editing && (
            <button onClick={() => void remove()} disabled={saving} style={{ ...tapGhost, color: "var(--bad)", flex: "none" }}>
              Delete
            </button>
          )}
          <button onClick={() => void save()} disabled={saving || effective <= 0} style={{ ...tapPrimary, flex: 1 }}>
            {effective <= 0 ? "Finish the length…" : editing ? "Save" : `Log ${durationLabel(effective)}`}
          </button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 6 }}>
        <div style={{ borderBottom: "1px solid var(--tint2)" }}>
          <PickerRow
            label="Project"
            value={projectId}
            onChange={(v) => { setProjectId(v); setItemId(""); }}
            options={[{ value: "", label: "— No project" }, ...selectable.map((p) => ({ value: p.id, label: p.client ? `${p.name} · ${p.client}` : p.name }))]}
            color={project?.color}
            primary={project?.name}
            secondary={task?.title || project?.client}
            placeholder="No project"
          />
        </div>

        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What did you work on?"
          aria-label="Note"
          style={field}
        />

        <TaskPicker value={itemId} onChange={setItemId} groups={taskGroups} disabled={!projectId} />

        {activityOptions.length > 0 && (
          <select
            value={activity}
            onChange={(e) => chooseActivity(e.target.value)}
            aria-label="Activity"
            style={{ ...field, color: activity ? "var(--ink)" : "var(--muted)" }}
          >
            <option value="">Activity…</option>
            {activityOptions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        )}

        <input
          type="date"
          value={day}
          max={toIsoLocal(new Date())}
          onChange={(e) => setDay(e.target.value || toIsoLocal(new Date()))}
          aria-label="Date"
          style={field}
        />

        <div style={{ display: "flex", gap: 6 }}>
          {CHIPS.map((m) => {
            const on = !hasCustom && minutes === m;
            return (
              <button
                key={m}
                onClick={() => { setMinutes(m); setCustom(""); }}
                style={{
                  flex: 1, minHeight: 46, borderRadius: 9, cursor: "pointer", fontSize: 14, fontWeight: 500,
                  border: `1px solid ${on ? "var(--accentLine)" : "var(--line2)"}`,
                  background: on ? "var(--accentTint)" : "var(--surface)",
                  color: on ? "var(--accent)" : "var(--ink2)",
                }}
              >{durationLabel(m)}</button>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="1:15, 90 or 1.5h"
            aria-label="Custom length"
            className="mono"
            style={{
              ...field, flex: 1, textAlign: "center",
              borderColor: !hasCustom ? "var(--line2)" : customOk ? "var(--accentLine)" : "var(--warn)",
              background: hasCustom ? "var(--accentTint)" : "var(--surface)",
            }}
          />
        </div>

        <KindPicker value={kind} choices={kindChoices(project, hasClient)} onChange={setKindPick} size="lg" fromProject={!kindPick} />
      </div>
    </Sheet>
  );
}
